import { useEffect, useState } from 'react';
import { CustomSelect } from '@/components/form/CustomSelect';
import { Spinner } from '@/components/Spinner';
import { Pager } from '@/components/Pager';
import { SortableHeader } from '@/components/SortableHeader';
import { useColumnSort } from '@/lib/tableSort';
import { formatDateTime } from '@/lib/clientFormat';
import { fetchAdminEvents, fetchAdminEventRoles, type AdminEventRow } from '@/api/events';
import type { ClientsPageMeta } from '@/api/clients';

type SortKey = 'created_at' | 'status' | 'full_name' | 'role';

/** Журнал событий админки — 1:1 таблица старого Orchid EventListAdminLayout, сортировка по дате/результату/пользователю/роли + фильтр по роли (то, что просили). */
export function AdminEventsTable() {
  const [status, setStatus] = useState('');
  const [role, setRole] = useState('');
  const [roles, setRoles] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const sort = useColumnSort<SortKey>();
  const [rows, setRows] = useState<AdminEventRow[] | null>(null);
  const [meta, setMeta] = useState<ClientsPageMeta | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sortParam = sort.key ? (sort.dir === 'desc' ? `-${sort.key}` : sort.key) : undefined;

  useEffect(() => {
    fetchAdminEventRoles()
      .then(setRoles)
      .catch(() => {
        /* фильтр по роли просто не покажет вариантов — не критично для самого списка */
      });
  }, []);

  useEffect(() => {
    setPage(1);
  }, [status, role, sort.key, sort.dir]);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    fetchAdminEvents({ status: status || undefined, role: role || undefined, sort: sortParam, page })
      .then(({ items, meta: pageMeta }) => {
        if (cancelled) return;
        setRows(items);
        setMeta(pageMeta);
      })
      .catch(() => {
        if (!cancelled) setError('Не удалось загрузить журнал событий.');
      });
    return () => {
      cancelled = true;
    };
  }, [status, role, sortParam, page]);

  return (
    <div>
      <div className="filter-row">
        <div className="group-select">
          <CustomSelect
            value={status}
            onChange={setStatus}
            options={[
              { value: '', label: 'Все результаты' },
              { value: 'Успешно', label: 'Успешно' },
              { value: 'Ошибка', label: 'Ошибка' },
            ]}
          />
        </div>
        <div className="group-select">
          <CustomSelect
            value={role}
            onChange={setRole}
            options={[{ value: '', label: 'Все роли' }, ...roles.map((r) => ({ value: r, label: r }))]}
          />
        </div>
      </div>

      {error && <p className="field-error">{error}</p>}

      {rows === null ? (
        <div className="panel-loading">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <p className="cell-muted">События не найдены.</p>
      ) : (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <SortableHeader label="Дата и время" sortKey="created_at" sort={sort} onSort={sort.toggle} />
                  <th>Событие</th>
                  <th>Объект</th>
                  <SortableHeader label="Результат" sortKey="status" sort={sort} onSort={sort.toggle} />
                  <SortableHeader label="Пользователь" sortKey="full_name" sort={sort} onSort={sort.toggle} />
                  <SortableHeader label="Роль" sortKey="role" sort={sort} onSort={sort.toggle} />
                  <th>E-mail</th>
                  <th>IP-адрес</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="cell-muted tabular">{formatDateTime(row.createdAt)}</td>
                    <td>{row.name ?? '–'}</td>
                    <td>{row.object ?? '–'}</td>
                    <td>
                      <span className={`badge badge--${row.status === 'Успешно' ? 'good' : 'crit'}`}>
                        <span className="badge__dot" />
                        {row.status ?? '–'}
                      </span>
                    </td>
                    <td>{row.fullName ?? '–'}</td>
                    <td>{row.role ?? '–'}</td>
                    <td>{row.email ?? '–'}</td>
                    <td>{row.ipAddress ?? '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {meta && meta.lastPage > 1 && (
            <div className="table-footer">
              <span className="table-footer__status">
                Страница {meta.currentPage} из {meta.lastPage} · всего {meta.total}
              </span>
              <Pager page={meta.currentPage} lastPage={meta.lastPage} onPageChange={setPage} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
